import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Wifi, WifiOff, QrCode, RefreshCw, AlertCircle, CheckCircle2, Phone, Users } from 'lucide-react';
import { useSharedWhatsAppSession, WhatsAppProvider } from '@/hooks/useSharedWhatsAppSession';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

export function SharedWhatsAppSession() {
  const {
    session,
    loading,
    isConnecting,
    qrCode,
    error,
    isConnected,
    phoneNumber,
    provider,
    connect,
    disconnect,
    checkStatus,
    setProvider,
    isChangingProvider,
    refetch,
    setQrCode,
  } = useSharedWhatsAppSession();

  const [showQRModal, setShowQRModal] = useState(false);
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);

  // Revalida estado real ao montar para não confiar em status stale no banco
  useEffect(() => {
    if (session?.id) {
      checkStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.id]);

  // Poll status when QR modal is open
  useEffect(() => {
    if (showQRModal && qrCode) {
      const interval = setInterval(async () => {
        const result = await checkStatus();
        if (result?.status === 'connected') {
          setShowQRModal(false);
          setQrCode(null);
          refetch();
        }
      }, 3000);
      
      setPollingInterval(interval);
      return () => clearInterval(interval);
    } else if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
  }, [showQRModal, qrCode, checkStatus, refetch, setQrCode]);

  const handleConnect = async () => {
    try {
      const result = await connect();
      console.log('[SharedSession] Connect result:', result);
      
      if (result?.qrCode) {
        console.log('[SharedSession] QR code received, length:', result.qrCode.length);
        setShowQRModal(true);
      } else if (result?.status === 'connected') {
        refetch();
      } else {
        console.log('[SharedSession] No QR code in response');
      }
    } catch (err) {
      console.error('Connection error:', err);
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnect();
      refetch();
    } catch (err) {
      console.error('Disconnect error:', err);
    }
  };

  const handleRefreshStatus = async () => {
    await checkStatus();
    refetch();
  };

  const handleProviderChange = async (value: string) => {
    if (value === 'wppconnect' || value === 'evolution') {
      try {
        await setProvider(value as WhatsAppProvider);
      } catch (err) {
        console.error('Error changing provider:', err);
      }
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-full ${isConnected ? 'bg-green-500/20' : 'bg-muted'}`}>
                <Users className={`h-5 w-5 ${isConnected ? 'text-green-500' : 'text-muted-foreground'}`} />
              </div>
              <div>
                <CardTitle className="text-lg">Sessão Central do WhatsApp</CardTitle>
                <CardDescription>
                  Número compartilhado para todos os usuários enviarem mensagens
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge 
                variant={provider === 'evolution' ? 'default' : 'secondary'} 
                className="text-xs"
              >
                {provider === 'evolution' ? 'Evolution' : 'WPPConnect'}
              </Badge>
              <Badge variant={isConnected ? 'default' : 'secondary'} className="flex items-center gap-1">
                {isConnected ? (
                  <>
                    <Wifi className="h-3 w-3" />
                    Conectado
                  </>
                ) : (
                  <>
                    <WifiOff className="h-3 w-3" />
                    Desconectado
                  </>
                )}
              </Badge>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Provider Selector */}
          <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg border">
            <label className="text-sm font-medium min-w-fit">Provider:</label>
            <Select 
              value={provider}
              onValueChange={handleProviderChange}
              disabled={isConnected || isConnecting || isChangingProvider}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Selecione o provider" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="wppconnect">
                  WPPConnect
                </SelectItem>
                <SelectItem value="evolution">
                  Evolution API
                </SelectItem>
              </SelectContent>
            </Select>
            {isChangingProvider && (
              <Loader2 className="h-4 w-4 animate-spin" />
            )}
            {isConnected && (
              <span className="text-xs text-muted-foreground">
                Desconecte para trocar o provider
              </span>
            )}
          </div>

          {isConnected && phoneNumber && (
            <div className="flex items-center gap-2 p-3 bg-green-500/10 rounded-lg border border-green-500/20">
              <Phone className="h-4 w-4 text-green-600" />
              <span className="font-medium text-green-700">{phoneNumber}</span>
              <CheckCircle2 className="h-4 w-4 text-green-600 ml-auto" />
            </div>
          )}

          {isConnected ? (
            <div className="space-y-4">
              <Alert>
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertDescription>
                  <strong>Número central ativo!</strong> Todos os usuários do sistema podem enviar mensagens 
                  por este número sem precisar escanear QR Code.
                </AlertDescription>
              </Alert>

              <div className="flex gap-2">
                <Button variant="outline" onClick={handleRefreshStatus} className="flex-1">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Verificar Status
                </Button>
                <Button variant="destructive" onClick={handleDisconnect}>
                  <WifiOff className="h-4 w-4 mr-2" />
                  Desconectar
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Conecte o número central para permitir que todos os usuários enviem mensagens sem precisar 
                  ter sua própria sessão do WhatsApp.
                </AlertDescription>
              </Alert>

              <Button 
                onClick={handleConnect} 
                disabled={isConnecting || isChangingProvider}
                className="w-full"
              >
                {isConnecting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Conectando...
                  </>
                ) : (
                  <>
                    <QrCode className="h-4 w-4 mr-2" />
                    Conectar Número Central
                  </>
                )}
              </Button>
            </div>
          )}

          <div className="text-xs text-muted-foreground border-t pt-4 mt-4">
            <p className="font-medium mb-1">Como funciona:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Somente administradores podem conectar/desconectar o número central</li>
              <li>Todos os usuários autenticados podem enviar mensagens por este número</li>
              <li>As mensagens são rastreadas por quem enviou</li>
              <li>O celular com o WhatsApp deve permanecer conectado à internet</li>
              <li>
                <strong>Provider:</strong> {provider === 'evolution' ? 'Evolution API' : 'WPPConnect'} - 
                pode ser alterado antes de conectar
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* QR Code Modal */}
      <Dialog open={showQRModal} onOpenChange={setShowQRModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Escaneie o QR Code</DialogTitle>
            <DialogDescription>
              Abra o WhatsApp no celular e escaneie o código abaixo
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex flex-col items-center gap-4 py-4">
            {qrCode ? (
              <div className="bg-white p-4 rounded-lg">
                <img 
                  src={qrCode} 
                  alt="QR Code" 
                  className="w-64 h-64"
                />
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Gerando QR Code...</span>
              </div>
            )}

            <p className="text-sm text-muted-foreground text-center">
              Após escanear, aguarde a conexão ser estabelecida automaticamente.
            </p>

            <Button variant="outline" onClick={() => setShowQRModal(false)}>
              Cancelar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
